import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Sparkles, User, Bot, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// AI Knowledge Base for landing page visitors
const LANDING_KNOWLEDGE = {
    deposit: "To deposit funds on The Cybernetics:\n\n1. Create a free account or sign in\n2. Go to the Deposit page\n3. Select your cryptocurrency (BTC, ETH, USDT, or USDC)\n4. Copy the wallet address provided\n5. Send funds from your external wallet\n\nDeposits are usually confirmed within 10-30 minutes!",
    withdraw: "Withdrawing from The Cybernetics is simple:\n\n1. Sign in to your account\n2. Navigate to the Withdraw page\n3. Select currency and enter amount\n4. Provide your destination wallet address\n5. Confirm the transaction\n\nWithdrawals are processed quickly with minimal fees.",
    kyc: "KYC (Know Your Customer) verification helps secure your account:\n\n• Level 1: Basic info - instant\n• Level 2: ID verification - 24-48 hours\n• Level 3: Enhanced - higher limits\n\nHigher verification levels unlock larger withdrawal limits.",
    trading: "Trading on The Cybernetics:\n\n• Trade 50+ cryptocurrencies\n• Up to 100x leverage available\n• Long and short positions\n• Real-time charts and analytics\n• Stop-loss & take-profit orders\n\nOur CyberAI bot can also trade automatically for you!",
    bot: "CyberAI Trading Bot features:\n\n✓ 24/7 automated trading\n✓ Advanced AI algorithms\n✓ Customizable strategies\n✓ Risk management built-in\n✓ Track record of consistent returns\n\nLet AI work for you while you sleep!",
    fees: "Our competitive fee structure:\n\n• Trading fees: 0.1% per trade\n• Deposit fees: FREE\n• Withdrawal fees: Network fees only\n• No hidden charges\n• No monthly subscriptions",
    security: "Bank-grade security features:\n\n🔒 256-bit AES encryption\n🔒 Two-factor authentication (2FA)\n🔒 Cold storage for 95% of assets\n🔒 24/7 monitoring\n🔒 Regular security audits\n🔒 SOC2 compliant",
    about: "The Cybernetics is a next-generation crypto trading platform:\n\n📍 Headquartered in Limassol, Cyprus\n📜 CySEC regulated\n👥 125,000+ active traders\n💰 $5B+ trading volume\n🌍 Available in 190+ countries\n\nFounded in 2021, we combine AI technology with institutional-grade security.",
    signup: "Getting started is easy:\n\n1. Click 'Get Started' or 'Start Trading Now'\n2. Enter your email and create a password\n3. Verify your email\n4. Complete basic KYC (optional for small amounts)\n5. Deposit funds and start trading!\n\nThe whole process takes less than 5 minutes.",
    support: "We're here to help!\n\n📧 Email: support@thecybernetics.com\n📞 Phone: +357 25 030 271\n💬 Live chat: Available 24/7\n\nOur support team typically responds within 24 hours. For faster help, create an account to access priority support."
};

const LandingChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [isOpen]);

    // Show welcome message when chat opens
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            const welcomeMsg = {
                id: 'welcome',
                sender: 'bot',
                message: "👋 Welcome to The Cybernetics! I'm CyberBot, your AI assistant.\n\nI can help you learn about:\n• Platform features & trading\n• How to get started\n• Security & fees\n• Our AI trading bot\n\nAsk me anything!",
                created_at: new Date().toISOString()
            };
            setMessages([welcomeMsg]);
        }
    }, [isOpen, messages.length]);

    // AI Response Generator
    const generateAIResponse = (userMessage) => {
        const msg = userMessage.toLowerCase();
        
        // Check for signup/register intent
        if (msg.includes('sign up') || msg.includes('register') || msg.includes('create account') || msg.includes('get started') || msg.includes('join')) {
            return LANDING_KNOWLEDGE.signup;
        }
        
        // Knowledge-based responses
        if (msg.includes('deposit') || msg.includes('add fund') || msg.includes('send money') || msg.includes('fund')) {
            return LANDING_KNOWLEDGE.deposit;
        }
        if (msg.includes('withdraw') || msg.includes('cash out') || msg.includes('take money') || msg.includes('payout')) {
            return LANDING_KNOWLEDGE.withdraw;
        }
        if (msg.includes('kyc') || msg.includes('verify') || msg.includes('verification') || msg.includes('identity')) {
            return LANDING_KNOWLEDGE.kyc;
        }
        if (msg.includes('trade') || msg.includes('trading') || msg.includes('buy') || msg.includes('sell') || msg.includes('position') || msg.includes('leverage')) {
            return LANDING_KNOWLEDGE.trading;
        }
        if (msg.includes('bot') || msg.includes('auto') || msg.includes('cyberai') || msg.includes('algorithm') || msg.includes('ai trading')) {
            return LANDING_KNOWLEDGE.bot;
        }
        if (msg.includes('fee') || msg.includes('cost') || msg.includes('charge') || msg.includes('price') || msg.includes('commission')) {
            return LANDING_KNOWLEDGE.fees;
        }
        if (msg.includes('security') || msg.includes('safe') || msg.includes('protect') || msg.includes('secure') || msg.includes('hack')) {
            return LANDING_KNOWLEDGE.security;
        }
        if (msg.includes('about') || msg.includes('company') || msg.includes('who') || msg.includes('where') || msg.includes('cyprus') || msg.includes('regulate')) {
            return LANDING_KNOWLEDGE.about;
        }
        if (msg.includes('support') || msg.includes('help') || msg.includes('contact') || msg.includes('email') || msg.includes('phone')) {
            return LANDING_KNOWLEDGE.support;
        }
        if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey') || msg.includes('good')) {
            return "Hello! 👋 Great to meet you! I'm here to help you learn about The Cybernetics. What would you like to know?\n\n• How to get started\n• Trading features\n• Our AI bot\n• Security & fees";
        }
        if (msg.includes('thank')) {
            return "You're welcome! 😊 Ready to start trading? Click 'Get Started' to create your free account!\n\nAnything else I can help with?";
        }
        if (msg.includes('crypto') || msg.includes('bitcoin') || msg.includes('btc') || msg.includes('ethereum') || msg.includes('eth')) {
            return "We support 50+ cryptocurrencies including:\n\n• Bitcoin (BTC)\n• Ethereum (ETH)\n• Tether (USDT)\n• USD Coin (USDC)\n• Solana (SOL)\n• XRP, ADA, DOT, and many more!\n\nTrade with up to 100x leverage on major pairs.";
        }
        
        // Default response
        return "I can help you with:\n\n• 📝 How to sign up\n• 💰 Deposits & withdrawals\n• 📈 Trading features\n• 🤖 AI trading bot\n• 🔒 Security info\n• 💵 Fees & pricing\n\nJust ask, or click 'Get Started' to create your free account!";
    };

    const handleSend = () => {
        if (!inputValue.trim()) return;

        const userMessage = inputValue.trim();
        setInputValue('');

        // Add user message
        const userMsg = {
            id: `user-${Date.now()}`,
            sender: 'user',
            message: userMessage,
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, userMsg]);

        // Generate AI response with typing animation
        setIsTyping(true);
        setTimeout(() => {
            const aiResponse = generateAIResponse(userMessage);
            const botMsg = {
                id: `bot-${Date.now()}`,
                sender: 'bot',
                message: aiResponse,
                created_at: new Date().toISOString()
            };
            setMessages(prev => [...prev, botMsg]);
            setIsTyping(false);
        }, 800 + Math.random() * 700);
    };

    const quickReplies = [
        'How do I start?',
        'Trading features',
        'AI Bot info',
        'Is it secure?',
        'Fees?'
    ];

    const handleQuickReply = (text) => {
        setInputValue(text);
        inputRef.current?.focus();
    };

    return (
        <>
            {/* Floating Chat Button */}
            <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-[#5BA4A4] to-purple-500 rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(91,164,164,0.5)] hover:shadow-[0_0_35px_rgba(91,164,164,0.7)] transition-all"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                data-testid="landing-chat-btn"
            >
                <AnimatePresence mode="wait">
                    {isOpen ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <X size={24} className="text-white" />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="open"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ duration: 0.2 }}
                            className="relative"
                        >
                            <MessageCircle size={24} className="text-white" />
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#5BA4A4] animate-pulse" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.button>

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed bottom-24 right-4 sm:right-6 z-50 w-[340px] sm:w-[380px] max-w-[calc(100vw-2rem)] bg-[#0A0A0A]/95 backdrop-blur-xl border border-[rgba(91,164,164,0.3)] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                        style={{ height: '500px', maxHeight: 'calc(100vh - 140px)' }}
                        data-testid="landing-chat-window"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-[rgba(91,164,164,0.2)] bg-gradient-to-r from-[#5BA4A4]/10 via-purple-500/10 to-[#5BA4A4]/10">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-[#5BA4A4]/30 flex items-center justify-center border border-purple-500/30">
                                            <Sparkles size={20} className="text-purple-400" />
                                        </div>
                                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0a0a0a]" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white text-sm flex items-center gap-2">
                                            CyberBot
                                            <span className="text-[9px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded font-normal">AI</span>
                                        </h3>
                                        <p className="text-[11px] text-green-400">⚡ Online • Instant replies</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setIsOpen(false)} 
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    <X size={18} className="text-gray-400" />
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {messages.map((msg, index) => (
                                <motion.div
                                    key={msg.id || index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`flex items-end gap-2 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            msg.sender === 'user' 
                                                ? 'bg-[#5BA4A4]/20' 
                                                : 'bg-gradient-to-br from-purple-500/30 to-[#5BA4A4]/30'
                                        }`}>
                                            {msg.sender === 'user' ? (
                                                <User size={14} className="text-[#5BA4A4]" />
                                            ) : (
                                                <Sparkles size={14} className="text-purple-400" />
                                            )}
                                        </div>
                                        
                                        <div className={`rounded-2xl px-4 py-2.5 ${
                                            msg.sender === 'user'
                                                ? 'bg-gradient-to-r from-[#5BA4A4] to-[#4A9393] text-black rounded-br-md'
                                                : 'bg-[#1a1a1a]/80 text-white rounded-bl-md border border-purple-500/20'
                                        }`}>
                                            <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                            
                            {/* Typing indicator */}
                            {isTyping && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex items-center gap-2"
                                >
                                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500/30 to-[#5BA4A4]/30 flex items-center justify-center">
                                        <Sparkles size={14} className="text-purple-400" />
                                    </div>
                                    <div className="bg-[#1a1a1a]/80 rounded-2xl px-4 py-3 border border-purple-500/20">
                                        <div className="flex gap-1">
                                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                            
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Quick Replies */}
                        {messages.length <= 1 && (
                            <div className="px-4 pb-2">
                                <div className="flex flex-wrap gap-1.5">
                                    {quickReplies.map((reply, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleQuickReply(reply)}
                                            className="text-[11px] px-2.5 py-1.5 bg-gradient-to-r from-[rgba(91,164,164,0.1)] to-purple-500/10 border border-[rgba(91,164,164,0.3)] rounded-full text-[#5BA4A4] hover:border-[#5BA4A4] hover:bg-[rgba(91,164,164,0.2)] transition-all"
                                        >
                                            {reply}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Input */}
                        <div className="p-4 border-t border-[rgba(91,164,164,0.2)] bg-black/50">
                            <div className="flex gap-2">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                                    placeholder="Ask me anything..."
                                    className="flex-1 bg-[#1a1a1a] border border-[rgba(91,164,164,0.2)] rounded-xl text-white text-sm px-4 py-3 focus:outline-none focus:border-[#5BA4A4] placeholder-gray-500 transition-colors"
                                    data-testid="landing-chat-input"
                                />
                                <button
                                    onClick={handleSend}
                                    disabled={!inputValue.trim()}
                                    className="px-4 bg-gradient-to-r from-[#5BA4A4] to-purple-500 rounded-xl text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    data-testid="landing-chat-send"
                                >
                                    <Send size={18} />
                                </button>
                            </div>
                            
                            {/* CTA */}
                            <Link 
                                to="/register"
                                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#5BA4A4]/20 to-purple-500/20 border border-[#5BA4A4]/30 rounded-xl text-[#5BA4A4] text-sm font-semibold hover:bg-[#5BA4A4]/30 transition-all group"
                            >
                                Start Trading Now
                                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default LandingChat;
